// DW-CLIENT-PLACE-SURVIVES-REFRESH: the /client bounce must survive a
// refresh, so it lives in sessionStorage rather than router state alone.
// Never store tokens under these keys — path/intent markers only.
export const CLIENT_RETURN_TO_KEY = 'dw_return_to'
export const CLIENT_PLACE_INTENT_KEY = 'dw_place_intent'

// BRIEF-B11 return: tripwire mode on the sign-in starts only from this explicit
// link. A plain /client (a bookmark, a fresh tab, a leftover saved /client) is
// the ordinary sign-in: the tripwire form demands an authenticator code before
// sign-in, which a new owner does not have yet.
export const TRIPWIRE_LINK = '/client?place=tripwire'

// B11 review: the only return paths honoured are plain same-site /client/...
// paths that need no resolving (no dot segments, no encoded slashes or dots,
// no backslashes, no double slash), so "/client/../admin" or
// "/client//evil.com" are never followed.
export function safeClientReturn(p: string | null | undefined): string | null {
  if (!p || p.includes('\\') || /%2f|%5c|%2e/i.test(p) || p.startsWith('//')) return null
  let u: URL
  try {
    u = new URL(p, 'https://digitalwatch.invalid')
  } catch {
    return null
  }
  if (u.origin !== 'https://digitalwatch.invalid' || !u.pathname.startsWith('/client/')) return null
  if (u.pathname.includes('//')) return null
  const out = u.pathname + u.search + u.hash
  return out === p ? out : null
}

export function isTripwireLink(path: string | null | undefined): boolean {
  if (!path) return false
  const url = new URL(path, 'https://digitalwatch.invalid')
  return url.pathname === '/client' && url.searchParams.get('place') === 'tripwire'
}

// DW-CLIENT-PLACE-ON-SIGNIN: LoginPage places the tripwire itself, in the
// same tick the session is issued, rather than leaving it to HomeTripwiresCard
// to notice the intent (which can lose the race when ProtectedRoute swaps
// LoginPage out before the intent lands in storage). The one-time decoy from
// that placement is handed off here so HomeTripwiresCard can still show it if
// it mounts later. Never store tokens under this key either.
export const CLIENT_PLACED_DECOY_KEY = 'dw_placed_decoy'

// DW-CLIENT-PLACE-NO-CHOOSER: LoginPage's placeOnSignIn swallows every
// placeTripwire throw so the sign-in flow never fails on a tripwire-placement
// error. When the throw happens with a Bearer already in hand (a real POST
// failure, not the "no Bearer yet" race that HomeTripwiresCard's own
// wait-for-Bearer retry already covers), that failure must still reach the
// client — this key is how placeOnSignIn hands it off for HomeTripwiresCard
// to surface on mount.
export const CLIENT_PLACE_ERROR_KEY = 'dw_place_error'

// DW-CLIENT-PLACE-HANDOFF-SURVIVES-CONSUME: HomeTripwiresCard copies the
// one-time decoy (or error) out of CLIENT_PLACED_DECOY_KEY /
// CLIENT_PLACE_ERROR_KEY into its own local state on mount, but no longer
// removes those keys there — it removes them only once the client closes the
// sheet (Done / Close / Try again / dismiss). Without a separate sticky
// marker, the next ProtectedRoute render on exact /client (a zustand 401
// wipe, StrictMode, a parent re-render) would still need to distinguish "a
// place happened this sign-in" from "no place ever happened" even after the
// sheet closes and the payload keys are gone. This key is that tab-sticky
// gate — set once completeLogin has attempted a place for this sign-in,
// never removed by HomeTripwiresCard, read by both ProtectedRoute and
// ClientDashboard's hasPlaceHandoff() — never a token, never read to mint or
// restore a session.
export const CLIENT_PLACE_HANDOFF_KEY = 'dw_place_handoff'

// DW-CLIENT-PLACE-TAKEN-ONLY-AFTER-COPY: never a token — set only once a
// Link decoy has actually LEFT this tab via Copy (clipboard.writeText
// resolved, or the same-click execCommand fallback on the painted decoy
// returned true). A successful completeLogin POST is NOT taken — the decoy
// it returns still has to be copied by the client before it has gone
// anywhere. A showing CLIENT_PLACED_DECOY_KEY is also not taken on its own;
// it is merely not-yet-copied. completeLogin must not POST another Link
// while this key is set, OR while CLIENT_PLACED_DECOY_KEY still holds a
// decoy from an earlier place this tab hasn't copied out yet — either way a
// second POST would just burn a second one-time secret nobody asked for.
// Closing the tab drops it, same as every other key here.
export const CLIENT_DECOY_TAKEN_KEY = 'dw_decoy_taken'
